<template>
  <ModalDialog
    :header-text="currentOption?.title"
    contentClass="cart-modal-content"
    :canBeClosed="!formSubmitting"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <div v-if="!showApplicationForm" class="cart-modal-content">
        <Stepper
          :value="tabIndex"
          @change="handleTabChange"
          :items="tabs"
          :linear="true"
          data-cy="cart-stepper"
        />
        <div
          v-show="currentTab.type == TabType.Review"
          class="products-review-tab"
          data-cy="products-review-tab"
        >
          <ConfirmationPopup
            v-if="showProductRemoveAllConfirmation"
            anchor="products-header-container"
            :text="`Are you sure you want to remove all products?`"
            @closePopup="closeRemoveAllConfirmation"
            data-cy="products-remove-all-confirmation"
          />
          <div
            class="products-header-container"
            ref="products-header-container"
          >
            <span data-cy="total-products-label">
              Total product(s) in cart: {{ products.length }}</span
            >
            <div class="pull-right">
              <a
                class="products-header-container__remove"
                @click="showProductRemoveAllConfirmation = true"
                data-cy="products-remove-all-button"
              >
                <font-awesome-icon
                  class="question-icon"
                  icon="fa-solid fa-trash"
                />
                Empty Cart
              </a>
            </div>
          </div>
          <ConfirmationPopup
            v-if="showProductRemoveConfirmation"
            anchor="products-container"
            :text="productDeletionString"
            @closePopup="closeRemoveConfirmation"
            data-cy="products-remove-confirmation"
          />
          <div v-if="productsLoading" class="products-container">
            <CartItemTileSkeleton
              v-for="n in scheduleStore.getCartCount(type) || 10"
              :key="n"
              :type="type"
            />
          </div>
          <div v-else class="products-container" ref="products-container">
            <ProductsCartItemTile
              v-for="product in products"
              :key="product.Id"
              :product="product"
              :type="type"
              @editClicked="productEditClicked(product)"
              @removeClicked="productRemoveClicked(product)"
            />
          </div>
          <label
            v-if="holidayMessage"
            class="holiday-label"
            data-cy="holiday-label"
            >{{ holidayMessage }}</label
          >
          <div class="products-actions">
            <Button
              type="button"
              theme-color="primary"
              @click="changeTab(TabType.Submit)"
              data-cy="products-next-button"
            >
              Next
            </Button>
          </div>
        </div>
        <div
          v-show="currentTab.type == TabType.Submit"
          class="products-submit-tab"
          data-cy="products-submit-tab"
        >
          <form class="products-submit-form" @submit.prevent="onSubmit">
            <div v-if="isSubUser" class="form-control">
              <Checkbox
                id="notify-admin-checkbox"
                v-model="notifyAdmin"
                data-cy="notify-admin-checkbox"
              />
              <label
                class="form-label"
                for="notify-admin-checkbox"
                data-cy="notify-admin-label"
                >CC my administrator at: {{ adminEmail }}</label
              >
            </div>
            <div class="form-control">
              <label
                class="form-label"
                for="submit-email"
                data-cy="submit-email-label"
                >Send additional email to:
              </label>
              <input
                class="form-input form-input--email"
                type="email"
                id="submit-email"
                v-model="email"
                multiple
                data-cy="submit-email-input"
              />
            </div>
            <div class="form-control">
              <Checkbox
                id="save-email-checkbox"
                v-model="saveEmail"
                data-cy="save-email-checkbox"
              />
              <label
                class="form-label"
                for="save-email-checkbox"
                data-cy="save-email-label"
                >SAVE EMAIL ADDRESS</label
              >
            </div>
            <label class="form-control-title" data-cy="comments-label"
              >Comments</label
            >
            <textarea
              class="form-text-area"
              v-model="comments"
              rows="5"
              maxlength="500"
              data-cy="comments-input"
            ></textarea>
            <div v-if="currentOption?.urgentVisible" class="form-control">
              <Checkbox
                id="urgent-checkbox"
                v-model="urgent"
                data-cy="urgent-checkbox"
              />
              <label
                class="form-label"
                for="urgent-checkbox"
                data-cy="urgent-label"
                >Mark as URGENT</label
              >
            </div>
            <div class="products-actions">
              <Button
                type="button"
                theme-color="secondary"
                @click="changeTab(TabType.Review)"
                data-cy="products-previous-button"
              >
                Previous
              </Button>
              <LoadingButton
                type="submit"
                theme-color="primary"
                :loading="formSubmitting"
                data-cy="products-submit-button"
              >
                Submit
              </LoadingButton>
            </div>
          </form>
        </div>
      </div>
      <div
        v-else
        class="application-form-container"
        data-cy="application-form-container"
      >
        <Button
          class="return-to-cart-button"
          type="button"
          theme-color="secondary"
          :disabled="applicationFormSubmitting"
          @click="showApplicationForm = false"
          data-cy="return-to-cart-button"
        >
          Return to Cart
        </Button>
        <ProductApplicationForm
          :plants="plants"
          :product="applicationFormProduct"
          :ownsIds="applicationFormProductOwnsIds"
          :showBackAction="true"
          @closeForm="onFormClosed"
          @submittingChanged="(value: boolean) => (applicationFormSubmitting = value)"
        />
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { computed, onBeforeMount, ref } from "vue";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { Stepper } from "@progress/kendo-vue-layout";
import { useScheduleBStore } from "@/stores/useScheduleBStore";
import { api } from "@/api";
import type { ProductDisplayInCart } from "@/Domain/ProductDisplayInCart";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ProductsCartItemTile from "./ProductsCartItemTile.vue";
import { Checkbox } from "@progress/kendo-vue-inputs";
import { Button } from "@progress/kendo-vue-buttons";
import ConfirmationPopup from "@/shared/ui/inputs/ConfirmationPopup.vue";
import type { Response } from "@/api";
import type { IdAndName } from "@/Domain/IdAndName";
import ProductApplicationForm from "./ProductApplicationForm.vue";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import CartItemTileSkeleton from "@/shared/ui/skeletons/CartItemTileSkeleton.vue";
import { cartIcon, trackChangesAcceptIcon } from "@progress/kendo-svg-icons";

interface CartOption {
  type: SubApplicationType;
  title: string;
  urgentVisible: boolean;
}

enum TabType {
  Review = 0,
  Submit = 1,
}

const main = useMainStore();
const scheduleStore = useScheduleBStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  type: SubApplicationType;
  plants: IdAndName[];
}>();

const emit = defineEmits<{
  (e: "closeModal"): void;
}>();

const products = ref<ProductDisplayInCart[]>([]);
const productsLoading = ref(false);
const showProductRemoveConfirmation = ref(false);
const showProductRemoveAllConfirmation = ref(false);
const productToRemove = ref<ProductDisplayInCart | undefined>(undefined);
const formSubmitting = ref(false);

const productDeletionString = computed(() => {
  if (!productToRemove.value) return "";

  const productProps = [
    productToRemove.value.BaseName,
    productToRemove.value.BaseNumber,
    productToRemove.value.LabelName,
    productToRemove.value.LabelNumber,
  ].filter((p) => p);

  if (productProps.length === 0)
    return "Are you sure you want to delete selected product?";

  return `Are you sure you want to delete product ${productProps.join(" ")}?`;
});

const applicationFormSubmitting = ref(false);
const applicationFormProduct = ref<ProductDisplayInCart>(
  {} as ProductDisplayInCart
);
const showApplicationForm = ref(false);

const applicationFormProductOwnsIds = computed(() => {
  return products.value
    .filter((p) => p.Id === applicationFormProduct.value.Id)
    .map((p) => p.OwnsId);
});

const email = ref("");
const adminEmail = ref("");
const saveEmail = ref(false);
const comments = ref("");
const urgent = ref(false);
const isSubUser = ref(false);
const holidayMessage = ref("");
const notifyAdmin = ref(false);

const options = ref<CartOption[]>([
  {
    type: SubApplicationType.Add,
    title: "Add Product(s)",
    urgentVisible: true,
  },
  {
    type: SubApplicationType.Copy,
    title: "Copy Product(s)",
    urgentVisible: true,
  },
  {
    type: SubApplicationType.Terminate,
    title: "Terminate Product(s)",
    urgentVisible: false,
  },
]);

const currentOption = computed<CartOption | undefined>(() => {
  return options.value.find((o) => o.type === props.type);
});

const tabs = [
  {
    type: TabType.Review,
    label: "Review Products",
    svgIcon: cartIcon,
  },
  {
    type: TabType.Submit,
    label: "Submit",
    svgIcon: trackChangesAcceptIcon,
  },
];

const tabIndex = ref(0);

const currentTab = computed(() => {
  return tabs[tabIndex.value];
});

const handleTabChange = function (e: any) {
  tabIndex.value = e.value;
};

const productEditClicked = function (product: ProductDisplayInCart) {
  applicationFormProduct.value = product;
  showApplicationForm.value = true;
};

const productRemoveClicked = function (product: ProductDisplayInCart) {
  productToRemove.value = product;
  showProductRemoveConfirmation.value = true;
};

const closeRemoveConfirmation = function (confirmed: boolean) {
  showProductRemoveConfirmation.value = false;
  if (confirmed && productToRemove.value) {
    deleteProductFromCartAsync(productToRemove.value);
  }
};

const closeRemoveAllConfirmation = function (confirmed: boolean) {
  showProductRemoveAllConfirmation.value = false;
  if (confirmed) {
    deleteAllProductsFromCartAsync();
  }
};

const changeTab = function (tabType: TabType) {
  const index = tabs.findIndex((t) => t.type === tabType);
  if (index === -1) {
    console.error(
      `ProductCartModal.vue, changeTab(), tab with type ${tabType} not found`
    );
    return;
  }
  tabIndex.value = index;
};

const deleteProductFromCartAsync = async function (
  product: ProductDisplayInCart
) {
  let requestPromise: Promise<Response<boolean>>;

  if (props.type == SubApplicationType.Add) {
    requestPromise = api.get("VueDeleteProductFromAddCart", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: props.type.toString(),
      id: product.Id,
      ownsId: product.OwnsId,
    });
  } else {
    requestPromise = api.get("VueDeleteProductFromCart", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: props.type.toString(),
      productInCartId: product.Id.toString(),
      ownsId:
        props.type == SubApplicationType.Copy ? product.OwnsId.toString() : "",
    });
  }

  await handleResponsePromiseAsync(
    requestPromise,
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.success,
        message: "Product removed from cart",
        duration: 5000,
      });
      updateCartProductAsync();
    },
    "ProductCartModal.vue",
    "deleteProductFromCartAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Error removing product from cart. Please try again.",
        duration: 5000,
      });
    }
  );
};

const deleteAllProductsFromCartAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("VueDeleteAllFromCart", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: props.type.toString(),
    }),
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.success,
        message: "All products removed from cart",
        duration: 5000,
      });
      scheduleStore.setCartCount(props.type, 0);
      closeModal(true);
    },
    "ProductCartModal.vue",
    "deleteAllProductsFromCartAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Error removing products from cart. Please try again.",
        duration: 5000,
      });
    }
  );
};

const getCartProductsAsync = async function () {
  return api.get("VueGetAllMatchingProductsIncartSubApplication", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    subApplication: props.type.toString(),
  });
};

const updateCartProductAsync = async function () {
  productsLoading.value = true;
  await handleResponsePromiseAsync(
    getCartProductsAsync(),
    (data) => {
      products.value = data;
      scheduleStore.setCartCount(props.type, data.length);
    },
    "ProductCartModal.vue",
    "updateCartProductAsync"
  );

  // Close modal if cart is empty
  if (products.value.length === 0) {
    closeModal(true);
  }

  productsLoading.value = false;
};

const getUserProductCartEmailAsync = async function () {
  return api.get("GetPropertyValues", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    propertyName: "productCartEmail",
  });
};

const updateUserProductCartEmailAsync = async function () {
  return handleResponsePromiseAsync(
    getUserProductCartEmailAsync(),
    (data) => {
      if (data && data[0]) email.value = data[0];
    },
    "ProductCartModal.vue",
    "updateUserEmailAsync"
  );
};

const getCompanyTbAsync = async function () {
  return api.get("GetCompanyTb", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    subApplication: 3,
    isCalledFromUkd: false,
  });
};

const updateAdminEmailAsync = async function () {
  return handleResponsePromiseAsync(
    getCompanyTbAsync(),
    (data) => {
      if (data.companycontactsTbs && data.companycontactsTbs.length > 0) {
        adminEmail.value =
          data.companycontactsTbs.filter((c) => c.webCT)[0]?.Contact?.Email ||
          "";
      }
    },
    "ProductCartModal.vue",
    "updateAdminEmailAsync"
  );
};

const isSubUserAsync = async function () {
  return api.get("IsSubUser", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    userName: main.userLogin,
  });
};

const updateIsSubUserAsync = async function () {
  return handleResponsePromiseAsync(
    isSubUserAsync(),
    (data) => {
      isSubUser.value = data;
      notifyAdmin.value = true;
    },
    "ProductCartModal.vue",
    "updateIsSubUserAsync"
  );
};

const updateHolidayMessageAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetHolidayMessage", {}),
    (data) => {
      holidayMessage.value = data;
    },
    "ProductCartModal.vue",
    "updateHolidayMessageAsync"
  );
};

const generateUserEmailAddressForSubmissionAsync = async function () {
  let emailListString = "";
  if (notifyAdmin.value) {
    emailListString = `${email.value?.replaceAll(",", ";")};${
      adminEmail.value
    }`;
  } else {
    emailListString = email.value?.replaceAll(",", ";");
  }

  let userEmail = "";

  await handleResponsePromiseAsync(
    api.get("GetUserEmail", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      userName: main.userLogin,
      userIsOnline: true,
    }),
    (data) => {
      userEmail = data;
    },
    "ProductCartModal.vue",
    "generateUserEmailAddressForSubmissionAsync"
  );

  emailListString = `${userEmail};${emailListString}`;

  if (main.isEmulator)
    emailListString = `${userEmail};${emailListString};${adminEmail.value}`;
  else emailListString = `${userEmail};${emailListString}`;

  return emailListString;
};

const saveUserCartEmailAsync = async function () {
  return api.get("SetPropertyValues", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    propertyName: "productCartEmail",
    propertyValue: email.value,
  });
};

const submitProductsCartAsync = async function (emailListString: string) {
  return api.get("VueSubmitProductsCart", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    subApplication: props.type.toString(),
    userEmail: emailListString,
    realUserLogin: main.userLogin,
    comments: comments.value,
    isUrgentRequest: urgent.value,
  });
};

const onSubmit = async function () {
  if (formSubmitting.value) return;
  formSubmitting.value = true;

  if (saveEmail.value) await saveUserCartEmailAsync();

  const emailListString = await generateUserEmailAddressForSubmissionAsync();

  const errorNotification = {
    type: NotificationTypeEnum.error,
    message: "There was an error submitting your request. Please try again.",
    duration: 5000,
  };

  await handleResponsePromiseAsync(
    submitProductsCartAsync(emailListString),
    (data: string) => {
      if (data && data != "0") {
        notificationStore.notify({
          type: NotificationTypeEnum.success,
          message: `Your request has been submitted. Click Submissions menu option to review all previous submissions`,
          duration: 5000,
        });
        scheduleStore.setCartCount(props.type, 0);
        closeModal(true);
      } else {
        notificationStore.notify(errorNotification);
      }
    },
    "ProductCartModal.vue",
    "onSubmit",
    () => {
      notificationStore.notify(errorNotification);
    }
  );
  formSubmitting.value = false;
};

const onFormClosed = function (submitted: boolean) {
  showApplicationForm.value = false;
  if (submitted) updateCartProductAsync();
};

const closeModal = function (closed: boolean) {
  if (!closed) {
    notificationStore.notify({
      type: NotificationTypeEnum.info,
      message: "Please wait while products are being processed",
      duration: 5000,
    });
    return;
  }
  emit("closeModal");
};

onBeforeMount(() => {
  updateUserProductCartEmailAsync();
  updateAdminEmailAsync();
  updateIsSubUserAsync();
  updateHolidayMessageAsync();
  updateCartProductAsync();
});
</script>

<style lang="scss">
.cart-modal-content {
  max-width: 600px;
  width: 600px;

  @media (max-width: 720px) {
    max-width: 100%;
    width: 100%;
  }

  .products-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #333333;
    padding: 0 10px;
    margin-top: 36px;
    gap: 48px;

    &__remove {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333333;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;

      .question-icon {
        margin-right: 5px;
      }
    }
  }

  .products-review-tab {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 160px);

    @include media-breakpoint-up(sm) {
      display: block;
      height: auto;
    }
  }

  .products-container {
    overflow-y: auto;

    @include media-breakpoint-up(sm) {
      max-height: 360px;
    }
  }

  .products-submit-form {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
    padding: 0 10px;
    margin-top: 36px;
    gap: 16px;

    .form-control {
      display: flex;
      align-items: center;
      gap: 10px;

      &-title {
        margin-top: 16px;
      }

      .form-input--email {
        width: 50%;
      }
    }
  }

  .products-actions {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}

.application-form-container {
  max-width: 600px;
  padding: 2rem;

  .return-to-cart-button {
    margin-bottom: 24px;
  }
}
</style>
